import React from 'react'
import { Link } from 'react-router-dom'
import { getAdsRefreshExpiryBannerState, sellerProfile } from 'services/Utils'
import { Button } from 'shadcn-components/ui/button'
import { AlertTriangle, Clock } from 'lucide-react'

const AdsTokenExpiryBanner = ({ user }) => {
  const seller = user?.primary_seller
  if (!seller) {
    return null
  }

  const profile = sellerProfile(seller)
  if (!profile?.ads_refresh_expires_at) {
    return null
  }

  const state = getAdsRefreshExpiryBannerState({
    expiresAt: profile.ads_refresh_expires_at,
    sellerName: seller.name,
    sellerPublicId: seller.public_id,
    spapiAccess: seller.spapi_access,
  })

  if (!state) {
    return null
  }

  const isDestructive = state.variant === 'destructive'
  const isExpired = state.daysLeft <= 0

  // The sync auth job flips adapi_access to false the moment the token
  // permanently dies, so the expired banner must not require it — only the
  // pre-expiry countdown phases do (a disconnected profile has its own UI).
  if (!profile?.adapi_access && !isExpired) {
    return null
  }

  const sellerName = seller.name || 'your account'

  const message = isExpired ? (
    <>
      <span className="font-semibold">Amazon Ads access has expired</span>
      <span className="hidden sm:inline"> for </span>
      <span className="font-semibold sm:hidden"> — </span>
      <span className="font-semibold">{sellerName}</span>
      <span className="hidden sm:inline">
        {' '}
        — reconnect now to resume automation.
      </span>
    </>
  ) : isDestructive ? (
    <>
      Your Amazon Ads connection for{' '}
      <span className="font-semibold">{sellerName}</span> expires in{' '}
      <span className="font-semibold">
        {state.daysLeft} {state.daysLeft === 1 ? 'day' : 'days'}
      </span>
      <span className="hidden sm:inline">
        {' '}
        — reconnect now to avoid service interruption.
      </span>
      <span className="sm:hidden"> — reconnect now.</span>
    </>
  ) : (
    <>
      Your Amazon Ads connection for{' '}
      <span className="font-semibold">{sellerName}</span> expires in{' '}
      <span className="font-semibold">{state.daysLeft} days</span>. Reconnect to
      keep automation running.
    </>
  )

  // Same escalation ladder as the agent app, in this app's own banner
  // vocabulary: AlertCountdown's yellow family for the countdown phases
  // (fixed across themes, slate text like the trial bar) and the shadcn
  // destructive token only once access is actually broken.
  const barClass = isExpired
    ? 'bg-destructive border-destructive text-destructive-foreground'
    : isDestructive
      ? 'bg-yellow-300 border-yellow-400 text-slate-900'
      : 'bg-yellow-100 border-yellow-200 text-slate-800'

  const iconClass = isExpired
    ? 'text-destructive-foreground'
    : isDestructive
      ? 'text-slate-900'
      : 'text-slate-700'

  const Icon = isDestructive ? AlertTriangle : Clock

  return (
    <div
      role="alert"
      data-banner="ads-expiry"
      aria-live="polite"
      className={`flex flex-wrap items-center justify-center gap-x-3 gap-y-2 border-b px-4 py-2.5 font-geist text-sm shadow-sm ${barClass}`}
    >
      <div className="flex min-w-0 flex-1 items-center justify-center gap-2 sm:flex-initial">
        <Icon className={`h-4 w-4 shrink-0 ${iconClass}`} aria-hidden />
        <p className="text-center leading-snug">{message}</p>
      </div>
      <Button
        size="sm"
        variant={isExpired ? 'secondary' : 'outline'}
        className={
          isExpired
            ? 'shrink-0 bg-white text-destructive hover:bg-white/90'
            : isDestructive
              ? 'shrink-0 border-transparent bg-slate-900 text-white hover:bg-slate-800 hover:text-white'
              : 'shrink-0 border-yellow-300 bg-white text-slate-800 hover:bg-yellow-50 hover:text-slate-900'
        }
        asChild
      >
        <Link to={state.reconnectPath}>Reconnect</Link>
      </Button>
    </div>
  )
}

export default AdsTokenExpiryBanner
